import { z } from "zod";
import { addLocalDays, addWorkdays } from "./amsterdam.js";
import { MODULES, type ModuleKey, type OrgModuleStatus } from "./modules.js";
import type { OrgStatus } from "./platform.js";

/** Helpcenter en support (docs/HELPCENTER.md): berichten aan support en het supportteam. */

export const TICKET_KINDS = ["question", "problem", "idea", "billing"] as const;
export type TicketKind = (typeof TICKET_KINDS)[number];
export const TICKET_KIND_LABELS: Record<TicketKind, string> = {
  question: "Vraag",
  problem: "Probleem",
  idea: "Idee",
  billing: "Facturatie",
};

export const TICKET_STATUSES = ["new", "open", "on_hold", "done"] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
/** Labels voor support (inbox). */
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  new: "Nieuw",
  open: "Open",
  on_hold: "In de wacht",
  done: "Afgerond",
};

export const HOLD_REASONS = ["waiting_customer", "waiting_development", "waiting_third_party"] as const;
export type HoldReason = (typeof HOLD_REASONS)[number];
export const HOLD_REASON_LABELS: Record<HoldReason, string> = {
  waiting_customer: "Wacht op klant",
  waiting_development: "Wacht op ontwikkeling",
  waiting_third_party: "Wacht op derde partij",
};

/** Hoe lang een bericht in de wacht staat voordat het terugkomt in de inbox. */
export const HOLD_DURATIONS = ["until_reply", "1_workday", "3_workdays", "1_week"] as const;
export type HoldDuration = (typeof HOLD_DURATIONS)[number];
export const HOLD_DURATION_LABELS: Record<HoldDuration, string> = {
  until_reply: "Zodra de klant reageert",
  "1_workday": "Over 1 werkdag",
  "3_workdays": "Over 3 werkdagen",
  "1_week": "Over 1 week",
};

/** Moment waarop een bericht in de wacht terugkomt (`hold_until`); null = tot de klant reageert. */
export function nextHoldWake(from: Date, duration: HoldDuration): Date | null {
  switch (duration) {
    case "until_reply":
      return null;
    case "1_workday":
      return addWorkdays(from, 1);
    case "3_workdays":
      return addWorkdays(from, 3);
    case "1_week":
      return addLocalDays(from, 7);
  }
}

export const TICKET_CHANNELS = ["app", "web_form", "email"] as const;
export type TicketChannel = (typeof TICKET_CHANNELS)[number];
export const TICKET_CHANNEL_LABELS: Record<TicketChannel, string> = {
  app: "App",
  web_form: "Contactformulier",
  email: "E-mail",
};

export const SUPPORT_MESSAGE_TYPES = ["customer", "agent", "note", "system"] as const;
export type SupportMessageType = (typeof SUPPORT_MESSAGE_TYPES)[number];
/** Wat de klant te zien krijgt; interne notities en systeemregels nooit. */
export const CUSTOMER_VISIBLE_MESSAGE_TYPES: readonly SupportMessageType[] = ["customer", "agent"];

/** "Welk onderdeel?" bij een probleem. */
export const TICKET_COMPONENTS = [
  "plattegrond",
  "taken",
  "rapport",
  "assets",
  "keuringen",
  "mjop",
  "conditie",
  "account",
  "anders",
] as const;
export type TicketComponent = (typeof TICKET_COMPONENTS)[number];
export const TICKET_COMPONENT_LABELS: Record<TicketComponent, string> = {
  plattegrond: "Plattegrond en bevindingen",
  taken: "Taken",
  rapport: "Rapport",
  assets: "Assets en QR-codes",
  keuringen: "Onderhoud en keuringen",
  mjop: "MJOP",
  conditie: "Conditiemeting",
  account: "Inloggen en account",
  anders: "Iets anders",
};

/** SUP-0007; vanaf 10.000 gewoon langer (SUP-12345). */
export const formatTicketNumber = (n: number): string => `SUP-${String(n).padStart(4, "0")}`;
/** "SUP-0007", "sup-7" of "7" → 7; anders null. */
export function parseTicketNumber(s: string): number | null {
  const m = /^\s*(?:sup-?)?0*(\d{1,9})\s*$/i.exec(s);
  return m ? Number(m[1]) : null;
}

/** Status zoals de klant hem ziet. */
export function ticketVisibleStatus(status: TicketStatus, holdReason: HoldReason | null | undefined): string {
  switch (status) {
    case "new":
      return "Ontvangen";
    case "open":
      return "In behandeling";
    case "on_hold":
      return holdReason === "waiting_customer" ? "Wacht op jou" : "In behandeling";
    case "done":
      return "Afgerond";
  }
}

export const SUPPORT_ROLES = ["support", "support_admin"] as const;
export type SupportRole = (typeof SUPPORT_ROLES)[number];
export const SUPPORT_ROLE_LABELS: Record<SupportRole, string> = {
  support: "Support",
  support_admin: "Supportbeheerder",
};
/** support_admin mag alles wat support mag. */
export const supportRoleAtLeast = (have: SupportRole | null | undefined, need: SupportRole): boolean =>
  have === "support_admin" || (have === "support" && need === "support");

export const SUPPORT_EVENTS = ["new_unassigned", "assigned", "customer_reply", "due_soon", "mention"] as const;
export type SupportEvent = (typeof SUPPORT_EVENTS)[number];
export const SUPPORT_EVENT_LABELS: Record<SupportEvent, { l: string; t: string }> = {
  new_unassigned: { l: "Nieuw bericht, nog niet toegewezen", t: "Alle collega's die op Beschikbaar staan" },
  assigned: { l: "Bericht aan jou toegewezen", t: "Niet als je het zelf deed" },
  customer_reply: { l: "Klant reageert", t: "Op een bericht dat aan jou is toegewezen" },
  due_soon: { l: "Reactietijd bijna verlopen", t: "75% van de reactietijd voorbij en nog geen antwoord" },
  mention: { l: "Genoemd in een notitie", t: "Iemand noemt je met @naam" },
};
export const DEFAULT_SUPPORT_EVENT_PREFS: Record<SupportEvent, { inApp: boolean; email: boolean }> = {
  new_unassigned: { inApp: true, email: true },
  assigned: { inApp: true, email: true },
  customer_reply: { inApp: true, email: true },
  due_soon: { inApp: true, email: false },
  mention: { inApp: true, email: false },
};

export const EMAIL_DELIVERIES = ["immediate", "bundled_5min"] as const;
export type EmailDelivery = (typeof EMAIL_DELIVERIES)[number];
export const EMAIL_DELIVERY_LABELS: Record<EmailDelivery, string> = {
  immediate: "Direct",
  bundled_5min: "Gebundeld per 5 minuten",
};

export const SUPPORT_ROUTINGS = ["none", "round_robin"] as const;
export type SupportRouting = (typeof SUPPORT_ROUTINGS)[number];
export const SUPPORT_ROUTING_LABELS: Record<SupportRouting, string> = {
  none: "Niet toewijzen, iedereen pakt op",
  round_robin: "Om de beurt aan beschikbare collega's",
};

/** Herinnering en automatisch afronden bij "in de wacht" (dagen); null = nooit afronden. */
export const HOLD_FOLLOW_UPS = [
  { remind: 7, close: 14 },
  { remind: 3, close: 7 },
  { remind: null, close: null },
] as const;

export const DEFAULT_SUPPORT_SETTINGS = {
  routing: "none" as SupportRouting,
  responseTimeHours: 8,
  holdRemindDays: 7 as number | null,
  holdAutocloseDays: 14 as number | null,
};

/**
 * Context bij een bericht uit de app (§4.4). Alles optioneel: de server vult organisatie en gebruiker
 * zelf in en vertrouwt die niet uit de client.
 */
export const ticketContextSchema = z.object({
  buildingId: z.uuid().nullish(),
  buildingName: z.string().max(200).nullish(),
  floorId: z.uuid().nullish(),
  floorName: z.string().max(200).nullish(),
  route: z.string().max(500).nullish(),
  appVersion: z.string().max(50).nullish(),
  userAgent: z.string().max(500).nullish(),
  online: z.boolean().nullish(),
});
export type TicketContext = z.infer<typeof ticketContextSchema> & {
  orgName?: string | null;
  userName?: string | null;
};

export const TICKET_SUBJECT_MAX = 160;
export const TICKET_BODY_MAX = 20_000;
/** Bijlagen: PNG, JPG of PDF, maximaal 10 MB. */
export const SUPPORT_ATTACHMENT_TYPES = ["image/png", "image/jpeg", "application/pdf"] as const;
export const SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export interface SupportSettingsDto {
  routing: SupportRouting;
  responseTimeHours: number;
  holdRemindDays: number | null;
  holdAutocloseDays: number | null;
}
export const supportSettingsBodySchema = z
  .object({
    routing: z.enum(SUPPORT_ROUTINGS),
    responseTimeHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 10),
    holdRemindDays: z.number().int().min(1).max(60).nullable(),
    holdAutocloseDays: z.number().int().min(1).max(90).nullable(),
  })
  .refine((s) => s.holdAutocloseDays === null || s.holdRemindDays === null || s.holdAutocloseDays > s.holdRemindDays, {
    message: "Automatisch afronden moet na de herinnering komen",
    path: ["holdAutocloseDays"],
  });

export interface SupportMeResponse {
  userId: string;
  role: SupportRole;
  /** null = (nog) geen lid van het team (platformbeheerder zonder rij in het team). */
  available: boolean | null;
}

/** Eigen meldingsinstellingen (docs/HELPCENTER.md §8.2). */
export interface SupportNotificationSettingsDto {
  available: boolean;
  emailDelivery: EmailDelivery;
  workHoursOnly: boolean;
  muteWhenAway: boolean;
  events: Record<SupportEvent, { inApp: boolean; email: boolean }>;
  /** Staat mail aan op de server? */
  mailEnabled: boolean;
}
const eventPrefSchema = z.object({ inApp: z.boolean(), email: z.boolean() });
export const supportNotificationSettingsBodySchema = z.object({
  emailDelivery: z.enum(EMAIL_DELIVERIES),
  workHoursOnly: z.boolean(),
  muteWhenAway: z.boolean(),
  events: z.object(
    Object.fromEntries(SUPPORT_EVENTS.map((e) => [e, eventPrefSchema])) as Record<SupportEvent, typeof eventPrefSchema>,
  ),
});
export type SupportNotificationSettingsBody = z.infer<typeof supportNotificationSettingsBodySchema>;
export const supportAvailabilityBodySchema = z.object({ available: z.boolean() });

/** Melding in de app (bel in de inbox). */
export interface SupportNotificationDto {
  id: string;
  ticketId: string;
  ticketNumber: number;
  subject: string;
  event: SupportEvent;
  text: string;
  createdAt: string;
  read: boolean;
}
export interface SupportNotificationsResponse {
  items: SupportNotificationDto[];
  unread: number;
}
export const notificationsReadBodySchema = z.object({ ids: z.array(z.uuid()).max(200).optional() });

// ---------- API: berichten ----------

const trimmed = (max: number) => z.string().trim().max(max);

/** Openbaar contactformulier (docs/HELPCENTER.md §4.5); velden als tekst (HTML-formulier). */
export const contactFormSchema = z.object({
  kind: z.enum(TICKET_KINDS),
  name: trimmed(120).min(1, "Vul je naam in"),
  email: z.string().trim().toLowerCase().pipe(z.email("Ongeldig e-mailadres")),
  organization: trimmed(160).optional().default(""),
  subject: trimmed(TICKET_SUBJECT_MAX).min(1, "Vul een onderwerp in"),
  body: trimmed(TICKET_BODY_MAX).min(1, "Vul je bericht in"),
  component: z.enum(TICKET_COMPONENTS).optional(),
});
export type ContactForm = z.infer<typeof contactFormSchema>;

/** Nieuw bericht via Hulp in de app. */
export const appTicketBodySchema = z.object({
  kind: z.enum(TICKET_KINDS),
  subject: trimmed(TICKET_SUBJECT_MAX).min(1, "Vul een onderwerp in"),
  body: trimmed(TICKET_BODY_MAX).min(1, "Vul je bericht in"),
  component: z.enum(TICKET_COMPONENTS).optional(),
  context: ticketContextSchema.optional(),
});

export const customerReplyBodySchema = z.object({ body: trimmed(TICKET_BODY_MAX).min(1, "Vul je reactie in") });

export const agentMessageBodySchema = z.object({
  type: z.enum(["agent", "note"]),
  body: trimmed(TICKET_BODY_MAX).min(1, "Vul een tekst in"),
  /** Genoemde collega's in een notitie. */
  mentions: z.array(z.uuid()).max(20).default([]),
});
export const assignBodySchema = z.object({ userId: z.uuid().nullable() });
export const holdBodySchema = z.object({ reason: z.enum(HOLD_REASONS), duration: z.enum(HOLD_DURATIONS) });

export const INBOX_FILTERS = ["open", "mine", "unassigned", "on_hold", "done"] as const;
export type InboxFilter = (typeof INBOX_FILTERS)[number];
export const INBOX_FILTER_LABELS: Record<InboxFilter, string> = {
  open: "Open",
  mine: "Mijn",
  unassigned: "Niet toegewezen",
  on_hold: "In de wacht",
  done: "Afgerond",
};
export const inboxQuerySchema = z.object({
  filter: z.enum(INBOX_FILTERS).default("open"),
  q: z.string().trim().max(200).optional(),
});

export interface SupportPerson {
  id: string;
  name: string | null;
  email: string;
}

export interface AttachmentDto {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

export interface InboxRow {
  id: string;
  number: number;
  subject: string;
  kind: TicketKind;
  status: TicketStatus;
  holdReason: HoldReason | null;
  holdUntil: string | null;
  channel: TicketChannel;
  requesterName: string;
  requesterEmail: string;
  orgName: string | null;
  assignee: SupportPerson | null;
  dueAt: string | null;
  firstResponseAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Eerste regels van het laatste bericht van de klant of support. */
  preview: string;
}

export interface InboxResponse {
  rows: InboxRow[];
  counts: Record<InboxFilter, number>;
  /** Teller achter Inbox: nieuwe berichten zonder toewijzing. */
  newCount: number;
}

export interface SupportMessageDto {
  id: string;
  type: SupportMessageType;
  body: string;
  author: { name: string; email: string | null; isSupport: boolean } | null;
  mentions: string[];
  attachments: AttachmentDto[];
  createdAt: string;
}

export interface TicketDetail extends InboxRow {
  component: TicketComponent | null;
  context: TicketContext | null;
  orgId: string | null;
  requesterUserId: string | null;
  closedAt: string | null;
  messages: SupportMessageDto[];
}

/** Bericht zoals de klant het ziet: zonder notities en systeemregels. */
export interface CustomerTicketRow {
  id: string;
  number: number;
  subject: string;
  kind: TicketKind;
  /** ticketVisibleStatus */
  statusLabel: string;
  status: TicketStatus;
  waitingOnCustomer: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface CustomerTicketDetail extends CustomerTicketRow {
  messages: SupportMessageDto[];
}

export interface SupportAgentRow extends SupportPerson {
  role: SupportRole;
  available: boolean;
}

/** Realtime voor support (kanaal `support`) en de klant (eigen berichten). */
export interface SupportRealtimeEvent {
  type: "support.ticket.created" | "support.ticket.updated" | "support.message.created";
  ticketId: string;
}

export const SUPPORT_ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf";

// ---------- beheeracties, koppelen en het team (§4.3, §9, besluit 5) ----------

/** Account van de afzender in het zijpaneel: organisatie, modules en wat support kan doen. */
export interface SupportAccountDto {
  org: {
    id: string;
    name: string;
    status: OrgStatus;
    trialEndsAt: string | null;
    modules: { module: ModuleKey; status: OrgModuleStatus; trialEndsAt: string | null }[];
  } | null;
  /** De afzender heeft een account met toegang (lidmaatschap) en kan dus een inloglink krijgen. */
  requesterHasAccess: boolean;
  /** Er gaat mail uit (SMTP/MailerSend ingesteld). */
  mailEnabled: boolean;
}

export const TRIAL_EXTEND_DAYS = [7, 14, 30] as const;
export const supportActionBodySchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("login_link") }),
  z.object({
    action: z.literal("module"),
    module: z.enum(MODULES).refine((m) => m !== "buildings", "Gebouwen staat altijd aan"),
    status: z.enum(["active", "trial"]),
  }),
  z.object({ action: z.literal("trial"), days: z.union(TRIAL_EXTEND_DAYS.map((d) => z.literal(d))) }),
]);
export type SupportActionBody = z.infer<typeof supportActionBodySchema>;

export const supportLinkOrgBodySchema = z.object({ orgId: z.uuid() });
export interface SupportOrgHit {
  id: string;
  name: string;
  slug: string;
}

export interface SupportTeamRow extends SupportAgentRow {
  /** Platformbeheerders zijn altijd supportbeheerder en staan niet (per se) in het team. */
  platformAdmin: boolean;
}
export const supportTeamAddBodySchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email("Ongeldig e-mailadres")),
  role: z.enum(SUPPORT_ROLES),
});
export const supportTeamRoleBodySchema = z.object({ role: z.enum(SUPPORT_ROLES) });

/** Herinnering en automatisch afronden bij "In de wacht" (§9): 7/14, 3/7 of nooit. */
export const HOLD_PRESETS = [
  { remind: 7, autoclose: 14, label: "Na 7 dagen herinneren, na 14 dagen afronden" },
  { remind: 3, autoclose: 7, label: "Na 3 dagen herinneren, na 7 dagen afronden" },
  { remind: null, autoclose: null, label: "Nooit" },
] as const;
